import { useEffect, useRef } from "react";

/**
 * Interactive constellation canvas
 * - Deep space navy background
 * - White stars connected by lines when close
 * - Mouse interaction creates an attractive "comet" cursor
 */
export const Constellation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const setSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    // Build stars proportional to viewport
    const buildStars = () => {
      const density = Math.min(width, height) < 700 ? 0.00012 : 0.00018;
      const count = Math.floor(width * height * density);
      return Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.4 + 0.4,
        tw: Math.random() * Math.PI * 2,
        twS: 0.01 + Math.random() * 0.02,
      }));
    };

    let stars = buildStars();

    const onResize = () => {
      setSize();
      stars = buildStars();
    };

    const onMove = (e) => {
      const t = e.touches ? e.touches[0] : e;
      mouseRef.current.x = t.clientX;
      mouseRef.current.y = t.clientY;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    const LINK_DIST = 130;
    const MOUSE_DIST = 180;

    const render = () => {
      // background tint (subtle vignette)
      ctx.clearRect(0, 0, width, height);
      const grad = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) / 1.2
      );
      grad.addColorStop(0, "rgba(10, 18, 45, 0.0)");
      grad.addColorStop(1, "rgba(2, 5, 18, 0.55)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // update + draw stars
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        s.x += s.vx;
        s.y += s.vy;
        s.tw += s.twS;
        if (s.x < -10) s.x = width + 10;
        if (s.x > width + 10) s.x = -10;
        if (s.y < -10) s.y = height + 10;
        if (s.y > height + 10) s.y = -10;

        // mouse repulsion / attraction
        const mx = mouseRef.current.x;
        const my = mouseRef.current.y;
        const dx = s.x - mx;
        const dy = s.y - my;
        const dist = Math.hypot(dx, dy);
        if (mouseRef.current.active && dist < MOUSE_DIST) {
          const force = (1 - dist / MOUSE_DIST) * 0.6;
          s.x += (dx / (dist || 1)) * force;
          s.y += (dy / (dist || 1)) * force;
        }

        const twinkle = 0.55 + 0.45 * Math.sin(s.tw);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(235, 240, 255, ${0.45 + twinkle * 0.5})`;
        ctx.fill();
      }

      // draw connections
      for (let i = 0; i < stars.length; i++) {
        const a = stars[i];
        for (let j = i + 1; j < stars.length; j++) {
          const b = stars[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            const alpha = (1 - d / LINK_DIST) * 0.35;
            ctx.strokeStyle = `rgba(160, 190, 255, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        // link to mouse
        if (mouseRef.current.active) {
          const dx = a.x - mouseRef.current.x;
          const dy = a.y - mouseRef.current.y;
          const d = Math.hypot(dx, dy);
          if (d < MOUSE_DIST) {
            const alpha = (1 - d / MOUSE_DIST) * 0.55;
            ctx.strokeStyle = `rgba(180, 210, 255, ${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
            ctx.stroke();
          }
        }
      }

      // soft mouse glow
      if (mouseRef.current.active) {
        const g = ctx.createRadialGradient(
          mouseRef.current.x, mouseRef.current.y, 0,
          mouseRef.current.x, mouseRef.current.y, 90
        );
        g.addColorStop(0, "rgba(180, 210, 255, 0.20)");
        g.addColorStop(1, "rgba(180, 210, 255, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(mouseRef.current.x, mouseRef.current.y, 90, 0, Math.PI * 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="constellation-canvas" data-testid="constellation-canvas" />;
};

export default Constellation;
